import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Achievement } from '../../../pages/achievements/models/achievements';

@Injectable({
  providedIn: 'root',
})
export class AchievementNotificationService {
  private activeAchievementSubject = new BehaviorSubject<Achievement | null>(
    null,
  );
  activeAchievement$ = this.activeAchievementSubject.asObservable();

  private queue: Achievement[] = [];
  private totalCountSubject = new BehaviorSubject<number>(0);
  totalCount$ = this.totalCountSubject.asObservable();

  private currentIndexSubject = new BehaviorSubject<number>(0);
  currentIndex$ = this.currentIndexSubject.asObservable();

  /**
   * Enqueues and triggers celebration for one or more unlocked achievements.
   */
  showUnlockedAchievements(achievements: Achievement[]): void {
    if (!Array.isArray(achievements) || achievements.length === 0) {
      return;
    }

    // Filter out null/undefined items
    const valid = achievements.filter((a) => a && (a.title || a.id));
    if (valid.length === 0) return;

    if (!this.activeAchievementSubject.value) {
      // Nothing currently showing: initialize batch
      this.totalCountSubject.next(valid.length);
      this.currentIndexSubject.next(1);
      const [first, ...rest] = valid;
      this.queue = rest;
      this.activeAchievementSubject.next(first);
    } else {
      // Already displaying: append to queue and update total
      this.queue.push(...valid);
      this.totalCountSubject.next(this.totalCountSubject.value + valid.length);
    }
  }

  /**
   * Advances to the next queued achievement or closes the modal if the queue is empty.
   */
  next(): void {
    if (this.queue.length > 0) {
      const nextAchievement = this.queue.shift()!;
      this.currentIndexSubject.next(this.currentIndexSubject.value + 1);
      this.activeAchievementSubject.next(nextAchievement);
    } else {
      this.dismiss();
    }
  }

  /**
   * Dismisses all queued and active achievements.
   */
  dismiss(): void {
    this.queue = [];
    this.totalCountSubject.next(0);
    this.currentIndexSubject.next(0);
    this.activeAchievementSubject.next(null);
  }
}
